<template>
	<div class="partner-switcher" ref="root">
		<button
			type="button"
			class="user-logged-as partner-switcher__trigger"
			:aria-expanded="open ? 'true' : 'false'"
			:aria-label="`Switch ${partnerKindLabel}`"
			v-tooltip="`Switch ${partnerKindLabel}`"
			@click="toggle"
		>
			{{ user.logged_as }}
			<i class="la la-angle-down partner-switcher__caret" aria-hidden="true" />
		</button>
		<transition name="partner-switcher-fade">
			<div v-if="open" class="partner-switcher__panel" role="dialog" :aria-label="`Switch ${partnerKindLabel}`">
				<span class="partner-switcher__arrow" aria-hidden="true" />
				<p class="partner-switcher__title">Switch {{ partnerKindLabel }}</p>
				<OvInput
					v-model="keyword"
					:placeholder="`Search ${partnerKindLabel.toLowerCase()}…`"
					class="partner-switcher__search"
				/>
				<div class="partner-switcher__list" role="listbox">
					<p v-if="loading" class="partner-switcher__hint">Loading…</p>
					<p v-else-if="!options.length" class="partner-switcher__hint">No results</p>
					<button
						v-for="opt in options"
						:key="opt.v"
						type="button"
						class="partner-switcher__option"
						:class="{ 'partner-switcher__option--current': isCurrent(opt.v) }"
						role="option"
						:aria-selected="isCurrent(opt.v) ? 'true' : 'false'"
						:disabled="switching"
						@click="select(opt)"
					>
						<span class="partner-switcher__option-label">{{ opt.t }}</span>
						<i
							v-if="isCurrent(opt.v)"
							class="la la-check partner-switcher__check"
							aria-hidden="true"
						/>
					</button>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { useAuthStore } from "@/stores/auth";

const SEARCH_DEBOUNCE_MS = 250;

export default {
	name: "PartnerSwitcher",
	data() {
		return {
			open: false,
			keyword: "",
			options: [],
			loading: false,
			switching: false,
			searchTimer: null,
			searchSeq: 0,
		};
	},
	computed: {
		authStore() {
			return useAuthStore();
		},
		user() {
			return this.authStore.user;
		},
		partnerKind() {
			if (this.user?.advertiser_id) {
				return "advertiser";
			}
			if (this.user?.publisher_id) {
				return "publisher";
			}
			return null;
		},
		partnerKindLabel() {
			if (this.partnerKind === "advertiser") {
				return "Advertiser";
			}
			if (this.partnerKind === "publisher") {
				return "Publisher";
			}
			return "Partner";
		},
		currentId() {
			if (this.partnerKind === "advertiser") {
				return this.user?.advertiser_id;
			}
			if (this.partnerKind === "publisher") {
				return this.user?.publisher_id;
			}
			return null;
		},
	},
	watch: {
		keyword() {
			this.scheduleSearch();
		},
	},
	mounted() {
		document.addEventListener("click", this.onOutsideClick);
	},
	beforeUnmount() {
		document.removeEventListener("click", this.onOutsideClick);
		if (this.searchTimer) {
			clearTimeout(this.searchTimer);
			this.searchTimer = null;
		}
	},
	methods: {
		isCurrent(id) {
			return Number(id) === Number(this.currentId);
		},
		toggle() {
			if (this.open) {
				this.close();
			} else {
				this.openPanel();
			}
		},
		openPanel() {
			this.open = true;
			this.keyword = "";
			this.fetchOptions();
		},
		close() {
			this.open = false;
		},
		onOutsideClick(e) {
			if (this.$refs.root && !this.$refs.root.contains(e.target)) {
				this.close();
			}
		},
		scheduleSearch() {
			if (this.searchTimer) {
				clearTimeout(this.searchTimer);
			}
			this.searchTimer = setTimeout(() => {
				this.searchTimer = null;
				this.fetchOptions();
			}, SEARCH_DEBOUNCE_MS);
		},
		async fetchOptions() {
			if (!this.partnerKind) {
				return;
			}
			const seq = ++this.searchSeq;
			this.loading = true;
			try {
				const data = await this.$ovReq.get("user/switchOptions", {
					params: {
						keyword: this.keyword || undefined,
						limit: 50,
					},
				});
				if (seq !== this.searchSeq) {
					return;
				}
				this.options = data.options || [];
			} catch (e) {
				// $ovReq already shows an error toast
				if (seq === this.searchSeq) {
					this.options = [];
				}
			} finally {
				if (seq === this.searchSeq) {
					this.loading = false;
				}
			}
		},
		async select(opt) {
			const partnerKind = this.partnerKind;
			if (!partnerKind || this.switching || this.isCurrent(opt.v)) {
				this.close();
				return;
			}
			this.switching = true;
			try {
				const body = partnerKind === "advertiser"
					? { advertiser_id: opt.v }
					: { publisher_id: opt.v };
				const data = await this.$ovReq.post("user/switchPartner", body);
				const token = this.authStore.authToken;
				this.authStore.setUser({
					user: {
						...data.user,
						config: data.config,
					},
					token,
				});
				// Match Admin loginAs*: advertiser → dashboard, publisher → /app
				const home = partnerKind === "advertiser"
					? "/app/dashboard"
					: "/app";
				window.location.assign(home);
			} catch (e) {
				// $ovReq already shows an error toast
				this.switching = false;
			}
		},
	},
};
</script>

<style lang="scss">
.partner-switcher {
	position: relative;
	display: inline-flex;
	align-items: center;

	&__trigger {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		padding: 0;
		font: inherit;
		font-weight: var(--ds-font-weight-semibold);
		color: inherit;
		background: transparent;
		border: none;
		border-bottom: 1px dotted var(--ds-color-text-subtle);
		cursor: pointer;

		&:hover {
			color: var(--ds-color-primary);
			border-bottom-color: var(--ds-color-primary);
		}
	}

	&__caret {
		font-size: 12px;
		opacity: 0.7;
	}

	&__panel {
		position: absolute;
		top: calc(100% + 12px);
		right: 0;
		z-index: 300;
		width: min(320px, calc(100vw - 24px));
		padding: var(--ds-space-3);
		background: var(--ds-color-surface);
		border: 1px solid var(--ds-color-border);
		border-radius: var(--ds-radius-lg);
		box-shadow: var(--ds-shadow-lg);
		text-align: left;
		white-space: normal;
	}

	&__arrow {
		position: absolute;
		top: -6px;
		right: 24px;
		width: 12px;
		height: 12px;
		background: var(--ds-color-surface);
		border-left: 1px solid var(--ds-color-border);
		border-top: 1px solid var(--ds-color-border);
		transform: rotate(45deg);
	}

	&__title {
		margin: 0 0 var(--ds-space-2);
		font-size: var(--ds-font-size-sm);
		font-weight: var(--ds-font-weight-semibold);
		color: var(--ds-color-text);
	}

	&__search {
		margin-bottom: var(--ds-space-2);
	}

	&__list {
		max-height: 240px;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	&__hint {
		margin: 0;
		padding: var(--ds-space-3);
		font-size: var(--ds-font-size-xs);
		color: var(--ds-color-text-muted);
		text-align: center;
	}

	&__option {
		display: flex;
		align-items: center;
		gap: var(--ds-space-2);
		width: 100%;
		padding: var(--ds-space-2) var(--ds-space-3);
		text-align: left;
		font-family: inherit;
		font-size: var(--ds-font-size-xs);
		color: var(--ds-color-text);
		background: transparent;
		border: 1px solid transparent;
		border-radius: var(--ds-radius-sm);
		cursor: pointer;
		transition: background 0.15s, border-color 0.15s;

		&:hover:not(:disabled) {
			background: var(--ds-color-surface-hover);
			border-color: var(--ds-color-border-light);
		}

		&--current {
			background: var(--ds-color-primary-subtle);
			border-color: var(--ds-color-primary);
		}

		&:disabled {
			cursor: wait;
			opacity: 0.7;
		}
	}

	&__option-label {
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__check {
		flex-shrink: 0;
		font-size: 14px;
		color: var(--ds-color-primary-hover);
	}
}

.partner-switcher-fade-enter-active,
.partner-switcher-fade-leave-active {
	transition: opacity 0.2s ease, transform 0.2s ease;
}

.partner-switcher-fade-enter-from,
.partner-switcher-fade-leave-to {
	opacity: 0;
	transform: translateY(-6px);
}
</style>
